import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {withTranslation, WithTranslation} from "react-i18next";
import {User, UserState} from "../../store/user/types";
import {OverviewArticle} from "../../store/article/types";
import {EutrElement, EutrPreSupplier, EutrState} from "../../store/eutr/types";
import {setEditingEutrElements, setEditingEutrElement} from "../../store/eutr/actions";
import {compose} from "redux";
import {connect} from "react-redux";
import {AppState} from "../../store";
import axios from "axios";
import Paper from '@material-ui/core/Paper';
import {Button, createStyles, Theme, Toolbar, withStyles, WithStyles} from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Alert from "../../common/component/Alert";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import List from "@material-ui/core/List";
import ListItemIcon from '@material-ui/core/ListItemIcon';

type ElementsProps = RouteComponentProps & WithTranslation & WithStyles & {
    userState: UserState;
    eutrState: EutrState;
    setEditingEutrElements: typeof setEditingEutrElements;
    setEditingEutrElement: typeof setEditingEutrElement;
}

interface IElementsState {
    showError: boolean;
    errorMessage: string;
}

class EutrElements extends React.Component<ElementsProps, IElementsState> {

    state = {
        showError: false,
        errorMessage: ''
    }

    render() {
        const t = this.props.t;
        return (
            <Paper style={{padding: "10px", margin: "25px"}}>
                <Toolbar>
                    <Typography color="inherit" variant="h6">{t('EutrElements.title')}</Typography>
                </Toolbar>
                <Divider/>
                <List component="nav">
                    {this.props.eutrState.editingEutrElements.map((e: EutrElement, i: number) => ([
                        <ListItem key={i}>
                            <ListItemIcon>
                                <p>{e.sequentialNumber + 1}</p>
                            </ListItemIcon>
                            <ListItemText primary={e.description ? e.description : t('EutrElements.noDescription')}
                                          secondary={t('EutrElements.woodType') + (e.kindOfWoodScientific ? e.kindOfWoodScientific : t('EutrElements.noWoodType'))}/>
                            <ListItemSecondaryAction>
                                <IconButton aria-label={t('common.edit')}
                                            onClick={() => this.editElement(e)}>
                                    <EditIcon/>
                                </IconButton>
                                <IconButton aria-label={t('common.delete')}
                                            onClick={() => this.removeElement(i)}>
                                    <DeleteIcon/>
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>,
                        <Divider/>
                    ]))}
                </List>
                <Toolbar>
                    <div className={this.props.classes.toolbarButtonContainer}>
                        <Button variant="contained" color="primary" onClick={() => this.addElement()}
                                className={this.props.classes.button}>{t('common.add')}</Button>
                        <Button variant="contained" color="primary" onClick={() => this.save()}
                                className={this.props.classes.button}>{t('common.save')}</Button>
                        <Button variant="contained" onClick={() => this.cancel()}
                                className={this.props.classes.button}>{t('common.cancel')}</Button>
                    </div>
                </Toolbar>
                <Alert open={this.state.showError} message={this.state.errorMessage}
                       onClose={() => this.setState({showError: false})} type='error'/>
            </Paper>
        );
    }

    addElement() {
        let eutrElement = {sequentialNumber: this.props.eutrState.editingEutrElements.length};
        this.editElement(eutrElement);
    }

    editElement(element: EutrElement) {
        console.log('Editing the eutr element with seq. no. ' + element.sequentialNumber);
        this.props.setEditingEutrElement(JSON.parse(JSON.stringify(element))); // clone before edit
        this.props.history.push(`${this.props.match.url}/edit`)
    }

    removeElement(index: number) {
        console.log('Removing the eutr element with index. ' + index);
        let elements = this.props.eutrState.editingEutrElements;
        elements.splice(index, 1);
        this.recalculateSequentialNumbers(elements);
        this.props.setEditingEutrElements(elements);
    }

    recalculateSequentialNumbers(elements: Array<EutrElement>) {
        elements.forEach((e, i) => {
            e.sequentialNumber = i;
        });
    }

    save() {
        let articleNumbers = this.props.location.state.articles.map((a: OverviewArticle) => a.icin);
        let elements = this.props.eutrState.editingEutrElements;
        this.saveElements(articleNumbers, elements);
    }

    cancel() {
        this.props.setEditingEutrElements([]);
        this.props.history.goBack();
    }

    componentWillMount() {
        if (this.props.location.state.articles) {
            if (this.props.location.state.articles.length == 1 && this.props.eutrState.editingEutrElements.length == 0) {
                this.loadElements(this.props.location.state.articles[0].icin);
            }
        }
    }

    loadElements(icin: String) {
        let url = process.env.REACT_APP_SUPPLIER_SERVER_URL + '/suppliers/' + this.props.userState.user!.supplierId + '/articles/' + icin + '/eutrdeclarations/latest';
        axios.get(url)
            .then(res => {
                if (res.status == 200) {
                    if (res.data && res.data.elements) {
                        let eutrElements = res.data.elements;
                        console.log('Loaded ' + eutrElements.length + ' eutr elements of ' + icin);
                        this.props.setEditingEutrElements(eutrElements);
                    } else {
                        console.log('Failed to get eutr elements of ' + icin + ': no data!');
                        this.props.setEditingEutrElements([]);
                    }
                } else {
                    console.log('Failed to get eutr elements of ' + icin + ': server error ' + res.status + '!');
                    this.props.setEditingEutrElements([]);
                }
            })
            .catch(err => {
                console.log('Failed to get eutr elements of ' + icin + ': server error');
                console.log(err);
                this.props.setEditingEutrElements([]);
            })
    }

    saveElements(articleNumbers: string[], elements: EutrElement[]) {
        let url = process.env.REACT_APP_SUPPLIER_SERVER_URL + '/suppliers/' + this.props.userState.user!.supplierId + '/bulk/eutrdeclarations';
        axios.post(url, {articleNumbers: articleNumbers, elements: elements})
            .then(res => {
                if (res.status == 201) {
                    console.log('Saved ' + elements.length + ' eutr elements of ' + articleNumbers);
                    this.props.setEditingEutrElements([]);
                    this.props.history.goBack();
                } else {
                    console.log('Failed to save eutr elements: ' + res.status + '!');
                    this.setState({showError: true, errorMessage: this.props.t('EutrElements.saveFailed')});
                }
            })
            .catch(err => {
                console.log('Failed to save eutr elements');
                console.log(err);
                this.setState({showError: true, errorMessage: this.props.t('EutrElements.saveFailed')});
            })
    }
}

const styles = ({palette, spacing}: Theme) => createStyles({
    toolbarButtonContainer: {
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'flex-end'
    },
    button: {
        marginLeft: spacing.unit
    }
});

const mapStateToProps = (state: AppState) => ({
    userState: state.user,
    eutrState: state.eutr
});

export default compose(
    withRouter,
    withStyles(styles),
    withTranslation('common'),
    connect(mapStateToProps, {setEditingEutrElements, setEditingEutrElement})
)(EutrElements) as any;